import React, { Fragment, useContext, useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import ProductSelection from './ProductSelection'
import OrderDetailSelection from './OrderDetailSelection'
import { Product, ProductCategoryDetail } from '../../model'
import Button from '../Button/Button'
import OrderSummary from './OrderSummary'
import { createOrder, getMappedProductOptions } from '../../services/api/orders'
import { useLocalAddress } from '../../hooks/useLocalAddress'
import DynamicFaIcon from '../DynamicFaIcon/DynamicFaIcon'
import { TibiContext } from '../../context/ContextProvider'

const NewOrder: React.FunctionComponent = () => {
    //State
    const navigate = useNavigate()
    const { brand } = useContext(TibiContext)
    const [data, setData] = useState<any>({
        category: 0,
        invoice3rdParty: false,
        address: {
            street: '',
            city: '',
            extension: '',
            zipcode: '',
            houseNumber: ''
        },
        invoiceDetails: {
            name: '',
            emailAddress: '',
            street: '',
            city: '',
            extension: '',
            zipcode: '',
            houseNumber: ''
        },
        // surface: 0,
        products: []
    })
    const [isFormValid, setIsFormValid] = useState(false)
    const [thirdPartyFormValid, setThirdPartyFormValid] = useState(false)
    const [placingOrder, setPlacingOrder] = useState(false)
    const [selectedOrderCategory, setSelectedOrderCategory] = useState<ProductCategoryDetail>()
    const [currentStepIndex, setCurrentStepIndex] = useState(0)

    const [agreementChecked, setAgreementChecked] = useState(false)

    const { setLocalAddress } = useLocalAddress()

    const placeOrderEnabled = Boolean(
        !placingOrder &&
        data.products.length &&
        (!data.invoice3rdParty || (data.invoice3rdParty && thirdPartyFormValid)) &&
        (!brand?.termsAndConditionsUrl || agreementChecked)
    )
    useEffect(() => {
        setData({ ...data, products: [] })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrderCategory])

    //Handlers
    const nextStep = () => {
        setCurrentStepIndex(currentStepIndex + 1)
    }

    const prevStep = () => {
        setCurrentStepIndex(currentStepIndex - 1)
    }

    const placeOrder = async () => {
        setPlacingOrder(true)

        try {
            // get all product options that are added
            const productOptions = getMappedProductOptions(data.products)
            setLocalAddress(data.address)
            const res = await createOrder({
                invoice3rdParty: data.invoice3rdParty,
                invoiceDetails: data.invoice3rdParty ? data.invoiceDetails : null,
                category: Number(data.category),
                extension: data.address.extension,
                houseNumber: Number(data.address.houseNumber),
                zipcode: data.address.zipcode,
                packs: data.products.filter((a: any) => a._isPack).map((a: any) => a.id),
                products: data.products.filter((a: any) => !a._isPack).map((a: any) => a.id),
                remarks: data.remarks,
                productOptions: productOptions || []
            })

            if (res) {
                // Todo: show toast notificatio here that order is placed
                navigate('/dashboard/orders')
            }
        } catch (e) {
            console.log(e)
        }
        setPlacingOrder(false)
    }

    const handleFormChange = (formData: any, isValid: boolean) => {
        setData({ ...data, address: formData })
        setIsFormValid(isValid)
    }

    const onCategorySelect = (cat: ProductCategoryDetail) => {
        setSelectedOrderCategory(cat)
        setData({ ...data, category: cat.id })

        // Go directly to next step if no address data is requested
        // TODO prop exists in BE, but is not properly assigned yet
        // if (!cat.requestAddress) {
        //   nextStep();
        // }
    }

    const handleProductSelectionChange = (products: Product[]) => {
        setData({ ...data, products: products })
    }

    const handleRemarkChange = (remark: string) => {
        setData({ ...data, remarks: remark })
    }

    const removeProduct = (product: any) => {
        const currentList = [...data.products]
        // const productIndex = currentList.map((p: Product) => p.id).indexOf(product.id);
        // const productIndex = currentList.findIndex((a: any) => product._isPack ? product._isPack === true && product.id === a.id : product.id === a.id)
        const productIndex = currentList.findIndex((a: any) => product._isPack === a._isPack && a.id === product.id)
        currentList.splice(productIndex, 1)
        setData({ ...data, products: currentList })
    }

    const steps = [
        {
            displayName: 'Opdracht details',
            component: (
                <OrderDetailSelection
                    initialFormData={data.address}
                    handleFormChange={handleFormChange}
                    onCategorySelect={onCategorySelect}
                    selectedOrderCategory={selectedOrderCategory}
                />
            ),
            nextButton: selectedOrderCategory ? (
                <Button onClick={nextStep} disabled={!isFormValid}>
                    Volgende stap <DynamicFaIcon iconName='arrow-right' className='ml-2' />
                </Button>
            ) : null
        },

        {
            displayName: 'Producten kiezen',
            component: (
                <ProductSelection
                    selectedProducts={data.products}
                    address={data.address}
                    categoryId={data.category}
                    onSelectionChange={handleProductSelectionChange}
                    onNext={nextStep}
                />
            ),
            prevButton: (
                <Button onClick={prevStep} buttonStyle='white'>
                    <DynamicFaIcon iconName='arrow-left' className='mr-2' />
                    Vorige
                </Button>
            )
        },

        {
            displayName: 'Controleren & Plaatsen',
            component: (
                <OrderSummary
                    data={data}
                    handleProductSelectionChange={handleProductSelectionChange}
                    removeProduct={removeProduct}
                    onRemarkChange={handleRemarkChange}
                    onInvoiceChange={(isActive: boolean, values: any, isValid: boolean) => {
                        setData({
                            ...data,
                            invoice3rdParty: isActive,
                            invoiceDetails: isActive ? { ...data.invoiceDetails, ...values } : null
                        })
                        setThirdPartyFormValid(isValid)
                        // setData({ ...data, ...values })
                    }}
                    agreementChecked={agreementChecked}
                    onAgreementChange={setAgreementChecked}
                    termsAndConditionsUrl={brand?.termsAndConditionsUrl}
                />
            ),
            prevButton: (
                <Button onClick={prevStep} buttonStyle='white'>
                    <DynamicFaIcon iconName='arrow-left' className='mr-2' />
                    Vorige
                </Button>
            ),
            nextButton: (
                <Button disabled={!placeOrderEnabled} onClick={placeOrder}>
                    Plaats opdracht{' '}
                    <DynamicFaIcon iconName={placingOrder ? 'spinner' : 'arrow-right'} className='ml-2' />
                </Button>
            )
        }
    ]

    //Read
    const currentStepObject: any = steps[currentStepIndex]

    return (
        <div>
            <NavLink to='/dashboard/orders' className='mb-2 block'>
                <DynamicFaIcon iconName='arrow-left' className='mr-2 text-[--tb-primary]' />
                <span className='font-semibold underline'>Opdrachten</span>
            </NavLink>
            <div className='flex space-x-2 items-center justify-between mb-4'>
                <h2>Nieuwe opdracht</h2>
            </div>
            <div className='w-full flex gap-x-2 lg:gap-x-8 md:justify-between'>
                {steps.map((step, stepIndex) => {
                    return (
                        <Fragment key={stepIndex}>
                            <div className='flex items-center space-x-2'>
                                <div
                                    className={`leading-none flex-shrink-0 h-8 w-8 flex justify-center items-center border border-[--tb-fadedPrimary] ${
                                        currentStepIndex === stepIndex &&
                                        'bgPrimaryColor bg-opacity-100 ccText border-none'
                                    } font-semibold rounded-full`}
                                >
                                    {stepIndex + 1}
                                </div>
                                <span
                                    className={`font-semibold md:block ${currentStepIndex !== stepIndex && 'hidden'}`}
                                >
                                    {step.displayName}
                                </span>
                            </div>
                            <span className='border-b flex-grow self-center last:hidden border-dashed border-[--tb-fadedPrimary]'></span>
                        </Fragment>
                    )
                })}
            </div>
            <div>{currentStepObject.component}</div>
            <div className='flex justify-end py-4'>
                {currentStepObject.prevButton && <div className='mr-auto'>{currentStepObject.prevButton}</div>}
                {currentStepObject.nextButton}
            </div>
        </div>
    )
}

export default NewOrder
